import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArticleMeta from '../../types/ArticleMeta';
import ArticleCoverImage from './ArticleCoverImage';
import { Link as RouterLink } from 'react-router';
import { getRouterPathToAuthor } from '../../utilities/Links';
import MobileDrawer from './MobileDrawer';
import ComingSoonChip from './ComingSoonChip';
import dayjs from 'dayjs';
import SubscriberContentIcon from './SubscriberContentIcon';

interface DrawerProps {
  readonly meta: ArticleMeta;
  closeDrawer: () => void;
  openDrawer: () => void;
  readonly open: boolean;
}

const RenderStack = ({
  labelKey,
  children,
}: {
  labelKey: string;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const label = t(labelKey);

  return (
    <Stack direction='row' spacing={2}>
      <Typography
        variant='subtitle2'
        sx={{
          color: 'text.secondary',
          width: '120px',
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
};

export default function ArticleInformationDrawer({
  meta,
  closeDrawer,
  openDrawer,
  open,
}: Readonly<DrawerProps>) {
  const { t } = useTranslation();

  return (
    <MobileDrawer
      open={open}
      closeDrawer={closeDrawer}
      openDrawer={openDrawer}
      title={t('article.information')}
    >
      <Paper elevation={0} sx={{ marginBottom: 7 }}>
        <Stack
          direction='row'
          useFlexGap
          sx={{
            columnGap: 1,
            justifyContent: 'start',
            alignItems: 'center',
            marginBottom: 1,
          }}
        >
          {!meta.isPublic && (
            <SubscriberContentIcon sx={{ height: '1rem', width: '1rem' }} />
          )}
          <Typography variant='overline'>{meta.type}</Typography>
        </Stack>

        <Stack
          direction='row'
          spacing={3}
          sx={{
            justifyContent: 'space-between',
            paddingBottom: 2,
          }}
        >
          <Box>
            <Typography variant='h3'>{meta.title}</Typography>
          </Box>
          <Box
            sx={{
              alignContent: 'center',
              maxHeight: '100%',
              maxWidth: '30%',
            }}
          >
            <ArticleCoverImage
              category={meta.categories.primary}
              src={meta.coverImage}
            />
          </Box>
        </Stack>

        <Stack
          direction='column'
          spacing={2}
          sx={{
            gap: 2,
          }}
        >
          <RenderStack labelKey='article.published'>
            {!meta.isUpcoming && (
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                {dayjs(meta.releaseDate).format('D.M.YYYY')}
              </Typography>
            )}
            {meta.isUpcoming && <ComingSoonChip />}
          </RenderStack>

          {meta.authors.length > 0 && (
            <RenderStack labelKey='article.authors'>
              <Stack direction='column' spacing={1}>
                {meta.authors.map((author) => (
                  <Link
                    key={'author-' + author}
                    variant='body1'
                    component={RouterLink}
                    underline='hover'
                    to={getRouterPathToAuthor(author)}
                    sx={{
                      width: '100%',
                    }}
                  >
                    {author}
                  </Link>
                ))}
              </Stack>
            </RenderStack>
          )}

          <RenderStack labelKey='article.subjects'>
            {[...meta.categories.secondary, meta.categories.primary].map(
              (category) => (
                <Typography
                  variant='body2'
                  key={'category-' + category}
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {category}
                </Typography>
              ),
            )}
          </RenderStack>
        </Stack>
      </Paper>
    </MobileDrawer>
  );
}
